import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LANDING } from "@/constants/testIds";
import { ArrowUpRight, Search, MapPin } from "lucide-react";

/**
 * Premium marketplace hero.
 *
 * - Full-bleed sports background photo (dark) with a heavy overlay for
 *   headline contrast — matches the black/white brand tone.
 * - Big, front-and-center search bar (What + Where) — the fastest path from
 *   landing to a booked session.
 * - Two secondary CTAs kept for the "just browsing" and "become a coach" paths.
 */
// A high-contrast, cinematic sports image — chosen because the athlete's
// silhouette leaves the top of the frame clean for the headline.
const HERO_BG =
  "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=2000&q=85&auto=format&fit=crop";

export default function Hero() {
  const navigate = useNavigate();
  const [what, setWhat] = useState("");
  const [where, setWhere] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (what.trim()) params.set("q", what.trim());
    if (where.trim()) params.set("city", where.trim());
    navigate(`/marketplace${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <section
      id="top"
      className="relative isolate min-h-[92vh] md:min-h-[88vh] flex items-center overflow-hidden"
    >
      {/* Background image + overlay */}
      <img
        src={HERO_BG}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
      {/* Subtle grain — softens the photo, keeps it feeling editorial. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' /></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
        }}
      />

      <div className="relative w-full max-w-[1200px] mx-auto px-6 md:px-10 pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="max-w-[900px] mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 h-8 px-3.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[11.5px] tracking-[0.24em] uppercase font-semibold text-white/90"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
            Verified independent coaches
          </motion.div>

          <motion.h1
            data-testid={LANDING.heroHeadline}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-display font-medium leading-[0.95] tracking-[-0.05em] text-[48px] sm:text-[72px] md:text-[92px]"
          >
            Book a coach in
            <br className="hidden sm:block" />
            <span className="italic font-light text-white/90"> minutes.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 md:mt-8 font-body text-white/80 text-lg md:text-xl leading-relaxed max-w-[54ch] mx-auto"
          >
            Sports, skills, and personal growth — one price per session, no bundles, no gyms.
          </motion.p>

          {/* Search bar */}
          <motion.form
            onSubmit={submit}
            data-testid="hero-search-form"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-10 md:mt-12 mx-auto max-w-[720px]"
          >
            <div className="bg-white rounded-full p-1.5 md:p-2 shadow-2xl shadow-black/40 flex items-stretch">
              <label className="flex items-center gap-2 flex-1 min-w-0 pl-4 md:pl-6 pr-2 border-r border-slate-200">
                <Search className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={2.4} />
                <input
                  type="text"
                  value={what}
                  onChange={(e) => setWhat(e.target.value)}
                  placeholder="Boxing, tennis, guitar…"
                  aria-label="What do you want to learn"
                  data-testid="hero-search-what"
                  className="w-full h-12 md:h-14 bg-transparent text-slate-900 placeholder:text-slate-400 text-[14px] md:text-[15px] focus:outline-none"
                />
              </label>
              <label className="flex items-center gap-2 min-w-0 pl-4 pr-2 flex-1 md:flex-[0.9]">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={2.4} />
                <input
                  type="text"
                  value={where}
                  onChange={(e) => setWhere(e.target.value)}
                  placeholder="Where — or Online"
                  aria-label="Where"
                  data-testid="hero-search-where"
                  className="w-full h-12 md:h-14 bg-transparent text-slate-900 placeholder:text-slate-400 text-[14px] md:text-[15px] focus:outline-none"
                />
              </label>
              <button
                type="submit"
                data-testid="hero-search-submit"
                className="shrink-0 h-12 md:h-14 px-5 md:px-7 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13.5px] md:text-[14.5px] font-semibold inline-flex items-center gap-2 transition-colors"
              >
                <Search className="w-4 h-4 md:hidden" strokeWidth={2.4} />
                <span className="hidden md:inline">Search</span>
                <ArrowUpRight className="w-4 h-4 hidden md:inline" strokeWidth={2.4} />
              </button>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[12.5px] text-white/70">
              <span className="uppercase tracking-[0.22em] text-white/50 font-semibold">Popular</span>
              {["Boxing", "Swimming", "Tennis", "Cooking", "Music"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => {
                    setWhat(tag);
                    navigate(`/marketplace?q=${encodeURIComponent(tag)}`);
                  }}
                  className="hover:text-white transition-colors"
                >
                  {tag}
                </button>
              ))}
            </div>
          </motion.form>

          {/* Secondary CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <button
              onClick={() => navigate("/marketplace")}
              data-testid={LANDING.heroFindCoach}
              className="h-12 px-6 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white text-[13.5px] font-semibold inline-flex items-center gap-2 backdrop-blur-sm transition-colors"
            >
              Browse all coaches
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
            </button>
            <button
              onClick={() => navigate("/apply")}
              data-testid={LANDING.heroBecomeCoach}
              className="h-12 px-6 rounded-full text-white/80 hover:text-white text-[13.5px] font-semibold inline-flex items-center gap-1.5 transition-colors"
            >
              Become a coach →
            </button>
          </motion.div>
        </div>
      </div>

      {/* Bottom fade into next section */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white pointer-events-none" />
    </section>
  );
}
